import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { buildLoginUrl } from '@/lib/redirect';
import { useAuth } from '@/context/AuthContext';
import { useMyInvitations, ProducerInvitation } from '@/hooks/useMyInvitations';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import {
  Loader2,
  Mail,
  ArrowLeft,
  ArrowRight,
  Sprout,
  Calendar,
  Clock,
  Check,
  X,
  Search,
  UserCircle,
} from 'lucide-react';

function getRoleLabel(role: string): string {
  switch (role) {
    case 'admin':
      return 'Ägare / Medvärd';
    case 'staff':
      return 'Medarbetare';
    case 'staff':
      return 'Medarbetare';
    case 'viewer':
      return 'Läsbehörighet';
    case 'editor':
      return 'Redaktör';
    default:
      return role;
  }
}

function getStatusMessage(status: string): string | null {
  switch (status) {
    case 'accepted':
      return 'Du har accepterat denna inbjudan.';
    case 'declined':
      return 'Du tackade nej till denna inbjudan.';
    case 'expired':
      return 'Inbjudan har gått ut. Be admin skicka en ny.';
    case 'revoked':
      return 'Inbjudan drogs tillbaka.';
    default:
      return null;
  }
}

type AcceptState = 'idle' | 'submitting' | 'success' | 'error';

interface InvitationCardProps {
  invitation: ProducerInvitation;
  onAccept: (id: string) => Promise<unknown>;
  onDecline: (id: string) => Promise<unknown>;
  isProcessing: boolean;
}

function InvitationCard({ invitation, onAccept, onDecline, isProcessing }: InvitationCardProps) {
  const [acceptState, setAcceptState] = useState<AcceptState>('idle');
  const navigate = useNavigate();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleAccept = async () => {
    setAcceptState('submitting');
    try {
      await onAccept(invitation.invitation_id);
      setAcceptState('success');
      timeoutRef.current = setTimeout(() => {
        navigate('/producer');
      }, 1500);
    } catch {
      setAcceptState('idle');
    }
  };

  const handleGoToPortal = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    navigate('/producer');
  };

  const handleDecline = async () => {
    try {
      await onDecline(invitation.invitation_id);
    } catch {
      // Error handled in hook
    }
  };

  const invitedDate = new Date(invitation.invited_at);
  const expiresDate = new Date(invitation.expires_at);
  const daysUntilExpiry = Math.ceil((expiresDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  // Success state after accept
  if (acceptState === 'success') {
    return (
      <Card className="border-primary bg-primary/5">
        <CardContent className="flex flex-col items-center gap-4 p-6 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/20">
            <Check className="h-6 w-6 text-primary" />
          </div>
          <div>
            <p className="font-medium">✅ Du är nu värd för {invitation.producer_name}!</p>
            <p className="text-sm text-muted-foreground">
              Du får nu tillgång till värdportalen.
            </p>
          </div>
          <Button onClick={handleGoToPortal}>
            Gå till värdportalen
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  const statusMessage = getStatusMessage(invitation.status);
  const isActive = invitation.status === 'invited';
  const isBusy = isProcessing || acceptState === 'submitting';

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          {/* Left side: Producer info */}
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Sprout className="h-6 w-6 text-primary" />
            </div>
            <div className="space-y-1">
              <h3 className="font-medium text-lg">{invitation.producer_name}</h3>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">{getRoleLabel(invitation.role)}</Badge>
              </div>
              <div className="flex flex-col gap-1 text-sm text-muted-foreground">
                {invitation.invited_by_name && (
                  <span className="flex items-center gap-1">
                    <UserCircle className="h-3.5 w-3.5" />
                    Inbjuden av {invitation.invited_by_name}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  Inbjuden {format(invitedDate, 'd MMM yyyy', { locale: sv })}
                </span>
                {isActive && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {daysUntilExpiry > 0
                      ? `⏳ Giltig i ${daysUntilExpiry} ${daysUntilExpiry === 1 ? 'dag' : 'dagar'}`
                      : 'Utgår snart'}
                  </span>
                )}
              </div>
              {isActive && (
                <p className="text-xs text-muted-foreground pt-1">
                  Du får tillgång till värdportalen när du accepterar.
                </p>
              )}
            </div>
          </div>

          {/* Right side: Actions or status */}
          <div className="flex gap-2 sm:flex-col sm:items-end">
            {statusMessage ? (
              <p className="text-sm text-muted-foreground italic">{statusMessage}</p>
            ) : (
              <>
                <Button
                  onClick={handleAccept}
                  disabled={isBusy}
                  className="flex-1 sm:flex-initial"
                >
                  {acceptState === 'submitting' ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="mr-2 h-4 w-4" />
                  )}
                  Acceptera
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleDecline}
                  disabled={isBusy}
                  className="flex-1 sm:flex-initial text-muted-foreground"
                >
                  <X className="mr-2 h-4 w-4" />
                  Tacka nej
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function InvitationsSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2].map((i) => (
        <Card key={i}>
          <CardContent className="p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-4">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-4 w-60" />
                </div>
              </div>
              <div className="flex gap-2 sm:flex-col">
                <Skeleton className="h-10 w-28" />
                <Skeleton className="h-10 w-28" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <Mail className="h-8 w-8 text-muted-foreground" />
        </div>
        <div className="space-y-1">
          <h3 className="font-medium">Inga väntande inbjudningar</h3>
          <p className="text-sm text-muted-foreground max-w-xs">
            Du har inga inbjudningar till värdar just nu.
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/explore">
            <Search className="mr-2 h-4 w-4" />
            Utforska gårdar
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

export default function InvitationsPage() {
  const { user, isLoading: authLoading } = useAuth();
  const { invitations, isLoading, error, isAccepting, isDeclining, accept, decline } = useMyInvitations();
  const navigate = useNavigate();

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    navigate(buildLoginUrl(), { replace: true });
    return null;
  }

  const isProcessing = isAccepting || isDeclining;

  return (
    <div className="bg-background">

      <main className="container max-w-2xl py-8 px-4">
        <Button asChild variant="ghost" className="mb-6 -ml-2">
          <Link to="/account">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Tillbaka till Mitt konto
          </Link>
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Mina värdinbjudningar</CardTitle>
            <CardDescription>
              Här ser du inbjudningar att ansluta till värdar på Farmly. Välj om du vill acceptera eller tacka nej.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <InvitationsSkeleton />
            ) : error ? (
              <Card className="border-destructive">
                <CardContent className="p-6 text-center text-destructive">
                  <p>Kunde inte ladda inbjudningar.</p>
                  <p className="text-sm">{error.message}</p>
                </CardContent>
              </Card>
            ) : invitations.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="space-y-4">
                {invitations.map((invitation) => (
                  <InvitationCard
                    key={invitation.invitation_id}
                    invitation={invitation}
                    onAccept={accept}
                    onDecline={decline}
                    isProcessing={isProcessing}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
